import { useQuery } from '@tanstack/react-query';
import { taxTypesQuery } from '../../api/queries';
import { useI18n } from '../../i18n/I18nProvider';
import { Badge } from '../../ui/Feedback';
import styles from './Receipt.module.css';
import { DocStatusBadge, MockLabel, TaxStatusBadge, type TaxAxis } from '../sales/status';
import type { EtaEnvironment, ReceiptEtaStatus, ReceiptStatus } from '../../api/types';

/** The type code as our own label (falls back to the raw code, never guessed). */
export function useReceiptTypeLabel(): (code: string) => string {
  const { t } = useI18n();
  const labels = t.receipts.types as Record<string, string | undefined>;
  return (code) => labels[code] ?? code;
}

/** Business status of a receipt, reusing the billing-document status vocabulary. */
export function ReceiptStatusBadge({ value, testId }: { value: ReceiptStatus; testId?: string }) {
  return <DocStatusBadge value={value} testId={testId} />;
}

/** The ETA axis of a receipt (or a dash while there is none). */
export function ReceiptEtaBadge({
  value,
  testId,
}: {
  value: ReceiptEtaStatus | null;
  testId?: string;
}) {
  if (!value) return <span className={styles.subtle}>—</span>;
  return <TaxStatusBadge value={value as TaxAxis} testId={testId} />;
}

/**
 * The two-axis status cell: the receipt stage and the ETA status side by side, so the receipt's own
 * lifecycle is never read as the Tax Authority's decision (our own presentation).
 */
export function ReceiptTwoAxisCell({
  status,
  etaStatus,
  environment,
  notAccepted = false,
}: {
  status: ReceiptStatus;
  etaStatus: ReceiptEtaStatus | null;
  environment: EtaEnvironment | null | undefined;
  /** Invalid because it was refused at submission: said beside the chip (RB3R8-04). */
  notAccepted?: boolean | undefined;
}) {
  const { t } = useI18n();
  return (
    <span className={styles.axisCell}>
      <ReceiptStatusBadge value={status} testId="row-receipt-status" />
      <ReceiptEtaBadge value={etaStatus} testId="row-eta-status" />
      {status !== 'draft' ? <MockLabel environment={environment} /> : null}
      {notAccepted && etaStatus === 'invalid' ? (
        <span className={styles.axisNote} data-testid="row-not-accepted">
          {t.receipts.notAccepted}
        </span>
      ) : null}
    </span>
  );
}

/** A small badge used in the detail header for the receipt stage. */
export function ReceiptStageBadge({ value, testId }: { value: ReceiptStatus; testId?: string }) {
  return <DocStatusBadge value={value} testId={testId} />;
}

export function ReceiptTypeBadge({ code }: { code: string }) {
  const label = useReceiptTypeLabel();
  return <Badge tone="neutral">{label(code)}</Badge>;
}

/** The name of an ETA tax type (T1 …) from the official table, with its code (RB3R8-20). */
export function useTaxTypeName(): (code: string) => string {
  const { pick } = useI18n();
  const types = useQuery(taxTypesQuery);
  return (code) => {
    const type = types.data?.find((x) => x.code === code);
    return type ? `${pick(type.nameAr, type.nameEn)} (${code})` : code;
  };
}

/** Our label for an ETA payment-method code, with the code (RB3R8-08, RB3R8-20). */
export function usePaymentMethodLabel(): (code: string | null | undefined) => string {
  const { t } = useI18n();
  return (code) => (code ? `${t.receipts.paymentMethods[code] ?? code} (${code})` : '—');
}

/** The Cairo calendar date of an instant (`YYYY-MM-DD`): a date-only value stored at noon. */
export function cairoDateOf(iso: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Africa/Cairo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(iso));
  return parts;
}
